import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import StarDivider from "@/components/StarDivider";
import { Link } from "react-router-dom";
import { UserSearch, ClipboardCheck, Handshake, Megaphone, Users, ShieldCheck, Star } from "lucide-react";
import { WWD_VIDEO, WWD_POSTER } from "@/data/content";

// Small visual: shield with star inside (replaces the Indiana outline accent)
function ShieldStar() {
  return (
    <div className="relative flex items-center justify-center">
      <ShieldCheck size={56} strokeWidth={1.8} />
      <Star size={18} className="absolute fill-green-brand text-green-brand" style={{ top: "20px" }} />
    </div>
  );
}

const PILLARS = [
  {
    icon: UserSearch,
    title: "Recruit",
    text: "We identify and recruit qualified veterans and military family members who have a proven record of leadership and a desire to serve their communities.",
  },
  {
    icon: ClipboardCheck,
    title: "Evaluate",
    text: "Our team conducts a thorough vetting process that includes interviews, background checks, and a review of each candidate's commitment to democratic principles and Hoosier values.",
  },
  {
    icon: Handshake,
    title: "Endorse",
    text: "We proudly endorse the most qualified candidates who meet our high standards and are committed to putting service above self and families first.",
  },
  {
    icon: Megaphone,
    title: "Support",
    text: "We provide endorsed candidates with resources, connections, and strategic support to help them succeed from campaign to public service.",
  },
];

export default function WhatWeDo() {
  return (
    <>
      <SEO
        title="What We Do | Recruit, Endorse, and Support Veterans for Office"
        description="Veterans for Indiana recruits, evaluates, endorses, and supports veterans and military family members running for public office across Indiana."
        path="/what-we-do"
      />

      {/* HERO — navy left (with solid backing for legibility), looping background video right */}
      <section className="relative bg-navy-deep text-white overflow-hidden">
        {/* Right-side video — covers from 50% to right edge */}
        <div className="absolute right-0 top-0 bottom-0 w-full md:w-[55%] overflow-hidden" aria-hidden="true">
          <video
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={WWD_POSTER}
          >
            <source src={WWD_VIDEO} type="video/mp4" />
          </video>
          {/* navy fade overlay from left to right — solid on left, transparent on right */}
          <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/75 to-navy-deep/15" />
        </div>
        {/* Solid navy underlay for the left text column (always opaque, never overlapped by video) */}
        <div className="absolute left-0 top-0 bottom-0 right-1/2 md:right-[55%] bg-navy-deep" aria-hidden="true" />
        <div className="absolute inset-0 pattern-stars opacity-25" aria-hidden="true" />
        {/* bottom green strip */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-brand" />

        <div className="container-x relative py-20 lg:py-28">
          <div className="max-w-[460px]">
            <Reveal>
              <div className="flex items-center gap-3">
                <StarDivider color="#5BA63B" inline />
              </div>
              <h1 className="h-display text-5xl sm:text-6xl text-white mt-3">What We Do</h1>
              <span className="divider-green mt-5 inline-block" />
            </Reveal>
            <Reveal delay={1}>
              <p className="mt-6 text-white/90 leading-relaxed text-[15px]">
                Veterans for Indiana is dedicated to electing veterans and military families to offices across the state — from school board to Governor.
              </p>
              <p className="mt-4 text-green-brand leading-relaxed font-medium text-[15px]">
                We recruit, evaluate, and endorse leaders who will serve with honor, integrity, and a commitment to Hoosier families.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW WE MAKE AN IMPACT — white bg, large oval navy badges w/ green icons (matches reference) */}
      <section className="bg-white py-20" aria-labelledby="hwmi-title">
        <div className="container-x">
          <Reveal className="text-center">
            <h2 id="hwmi-title" className="h-display text-3xl sm:text-4xl text-navy">How We Make an Impact</h2>
            <div className="mt-3 flex justify-center"><StarDivider /></div>
          </Reveal>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 lg:divide-x lg:divide-gray-200">
            {PILLARS.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={(i + 1)} className="text-center lg:px-6">
                {/* Vertical oval navy badge with green icon inside */}
                <div
                  className="mx-auto flex items-center justify-center bg-navy text-green-brand shadow-md"
                  style={{ width: "120px", height: "152px", borderRadius: "50%" }}
                >
                  <Icon size={56} strokeWidth={2} />
                </div>
                <h3 className="font-display tracking-widest text-navy uppercase mt-6 text-2xl">{title}</h3>
                <span className="mt-3 inline-block h-[2px] w-12 bg-green-brand" />
                <p className="mt-5 text-[15px] text-navy-deep/85 leading-relaxed max-w-[260px] mx-auto">{text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP STARTS WITH SERVICE — dark navy band w/ Indiana outline + Get Involved */}
      <section className="relative bg-navy-deep text-white overflow-hidden">
        <div className="absolute inset-0 pattern-stars opacity-80" aria-hidden="true" />
        <div className="container-x relative py-16 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal className="flex items-start gap-6">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-green-brand/15 text-green-brand shrink-0 ring-2 ring-green-brand/40">
              <ShieldStar />
            </div>
            <div>
              <h2 className="h-display text-2xl sm:text-3xl text-white">Leadership Starts with Service.</h2>
              <p className="mt-3 text-white/85 leading-relaxed">
                We believe those who have already served our country are uniquely qualified to lead our communities.
              </p>
            </div>
          </Reveal>
          <Reveal delay={1} className="lg:border-l lg:border-white/15 lg:pl-10">
            <div className="flex items-center gap-2 text-green-brand">
              <Users size={20} />
              <span className="eyebrow text-green-brand">Get Involved</span>
            </div>
            <h2 className="h-display text-2xl sm:text-3xl mt-2 text-white">
              Help us elect more veterans and military families to build a stronger Indiana.
            </h2>
            <div className="mt-6">
              <Link to="/get-involved" className="btn btn-primary" data-testid="wwd-learn-more-cta">Learn More</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
